// Sky Lab: three experiments in one room. Orbits (real gravity), Star Life (mass decides the
// ending), Moon Phases (real Sun-Earth-Moon geometry). Lesson, then do it; Explore after.

import { h, clear } from '@kit/dom';
import { makeSurface } from '@kit/canvas';
import { attachPointer } from '@kit/pointer';
import { makeLoop } from '@kit/loop';
import type { GameModule, GameContext } from '@shell/types';
import { makeSun, makePlanet, step as orbitStep, isStable, circularSpeed, DT, type Body } from './orbit';

type Tab = 'orbits' | 'stars' | 'moon';
type MoonWant = 'full' | 'new' | 'first';

interface Step {
  tab: Tab;
  lesson: string;
  task: string;
  kind: 'orbit3' | 'escape' | 'moon' | 'starpick' | 'choice';
  moonWant?: MoonWant;
  starOk?: number[];          // acceptable star indices
  choices?: string[];
  answer?: number;
  sunMass?: number;           // orbit steps may set the Sun
  why: string;
}

function tierFor(level: number): 1 | 2 | 3 { return level <= 3 ? 1 : level <= 6 ? 2 : 3; }

const STEPS: Record<1 | 2 | 3, Step[]> = {
  1: [
    { tab: 'orbits', lesson: 'The Sun pulls on everything near it. A planet does not fall in because it is also moving sideways, fast. Pull plus sideways speed makes a circle.', task: 'Press on empty space, flick sideways, and let go. Get a planet to go around the Sun 3 times.', kind: 'orbit3', why: 'Just the right sideways speed makes a steady orbit. Too slow falls in, too fast flies away.' },
    { tab: 'moon', lesson: 'The Moon makes no light of its own. The Sun lights one half of it, and we see different amounts of that lit half as the Moon goes around Earth.', task: 'Drag the Moon until Earth sees a full moon, then tap Check.', kind: 'moon', moonWant: 'full', why: 'When the Moon is on the far side of Earth from the Sun, we see its whole lit face: a full moon.' },
    { tab: 'stars', lesson: 'Stars are born in clouds of gas. Small stars sip their fuel slowly and live a very long time. Big stars burn fast and bright and live short lives.', task: 'Pick a star that will live LONGER than our Sun.', kind: 'starpick', starOk: [0], why: 'The small red dwarf lives for trillions of years. Our Sun gets about 10 billion.' },
  ],
  2: [
    { tab: 'orbits', lesson: 'If a planet goes fast enough, the Sun cannot hold it. It flies off and never comes back. That speed is called escape speed.', task: 'Flick a planet hard enough that it leaves the system.', kind: 'escape', why: 'Past escape speed the Sun\'s pull is never enough to turn the planet around.' },
    { tab: 'moon', lesson: 'When the Moon sits between Earth and the Sun, the lit half faces away from us. We see nothing: a new moon.', task: 'Drag the Moon to make a new moon, then tap Check.', kind: 'moon', moonWant: 'new', why: 'Moon between Earth and Sun means we look at its dark side. New moon.' },
    { tab: 'stars', lesson: 'When a very big star runs out of fuel, its middle collapses in seconds and the star explodes. That is a supernova.', task: 'Pick a star that will EXPLODE at the end of its life.', kind: 'starpick', starOk: [2, 3], why: 'Only big stars, many times the Sun\'s mass, end in a supernova. Small ones fade to white dwarfs.' },
  ],
  3: [
    { tab: 'moon', lesson: 'Halfway from new to full, we see exactly half of the lit side. That is the first quarter: a quarter of the way around the orbit.', task: 'Make a first-quarter moon (waxing, half lit), then tap Check.', kind: 'moon', moonWant: 'first', why: 'At a right angle from the Sun, half the face we see is lit. Going from new toward full, that is first quarter.' },
    { tab: 'orbits', lesson: 'A heavier Sun pulls harder. To orbit a heavier Sun at the same distance, a planet needs MORE sideways speed.', task: 'The Sun is now heavy. Get a planet around it 3 times.', kind: 'orbit3', sunMass: 1.0, why: 'Stronger pull needs faster sideways speed to balance it. That is why inner planets move faster too.' },
    { tab: 'stars', lesson: 'Stars differ in color, size and brightness, but one thing decides how they live and die.', task: 'What decides how long a star lives and how it ends?', kind: 'choice', choices: ['Its mass', 'Its color name', 'How far it is from Earth', 'How many planets it has'], answer: 0, why: 'Mass sets everything: how hot the core gets, how fast fuel burns, and whether it fades, collapses or explodes.' },
  ],
};

// ---- star life data (from the widget) ----
interface Stage { t: 'cloud' | 'star' | 'shell' | 'boom' | 'wd' | 'ns' | 'bh'; n: string; a: string; d: string; r?: number; c?: string }
const CLOUD: Stage = { t: 'cloud', n: 'Star nursery', a: 'Where every star begins', d: 'A giant cloud of gas and dust. Gravity squeezes it into a ball until it gets hot enough to shine.' };
const BOOM: Stage = { t: 'boom', n: 'Supernova!', a: 'The collapse takes only seconds', d: 'The core collapses and the star explodes. For a few weeks it can shine brighter than a whole galaxy.' };
const STARS: { label: string; mass: string; st: Stage[] }[] = [
  { label: 'Small', mass: '0.2 Suns', st: [CLOUD, { t: 'star', n: 'Red dwarf', a: 'Lasts trillions of years', r: 14, c: '#E24B4A', d: 'A small, cool star. It sips its fuel so slowly that it can shine for trillions of years.' }, { t: 'wd', n: 'White dwarf', a: 'Far, far in the future', d: 'One day it will run out of fuel and shrink into a small ember that slowly fades.' }] },
  { label: 'Sun-size', mass: '1 Sun', st: [CLOUD, { t: 'star', n: 'Yellow star', a: 'Lasts about 10 billion years', r: 26, c: '#FAC775', d: 'A star like our Sun. It shines steadily by squeezing hydrogen into helium deep inside.' }, { t: 'star', n: 'Red giant', a: 'Lasts about 1 billion years', r: 80, c: '#D85A30', d: 'Running low on fuel, the star swells up huge and turns red. Our Sun will do this one day.' }, { t: 'shell', n: 'Planetary nebula', a: 'Lasts about 10,000 years', d: 'The star puffs its outer layers into space, making a glowing bubble of gas.' }, { t: 'wd', n: 'White dwarf', a: 'Cools for trillions of years', d: 'What is left is a hot core about the size of Earth. It slowly cools for the rest of time.' }] },
  { label: 'Big', mass: '12 Suns', st: [CLOUD, { t: 'star', n: 'Blue star', a: 'Lasts about 20 million years', r: 38, c: '#B5D4F4', d: 'A big, hot star. It burns its fuel fast, so it lives a short life.' }, { t: 'star', n: 'Red supergiant', a: 'Lasts about 1 million years', r: 105, c: '#D85A30', d: 'It swells to an enormous size. In the Sun\'s place it could swallow Earth and Mars.' }, BOOM, { t: 'ns', n: 'Neutron star', a: 'Lasts billions of years', d: 'The crushed core is only as wide as a city, but a teaspoon of it weighs as much as a mountain.' }] },
  { label: 'Giant', mass: '30 Suns', st: [CLOUD, { t: 'star', n: 'Blue giant', a: 'Lasts about 6 million years', r: 48, c: '#85B7EB', d: 'One of the biggest stars there is. It shines super bright and uses up its fuel very quickly.' }, { t: 'star', n: 'Red supergiant', a: 'Lasts about half a million years', r: 115, c: '#D85A30', d: 'Almost out of fuel, it swells up into one of the largest stars in the galaxy.' }, BOOM, { t: 'bh', n: 'Black hole', a: 'Lasts almost forever', d: 'The core collapses all the way. Its gravity is so strong that not even light can get out.' }] },
];

function phaseName(e: number, waxing: boolean): string {
  const f = (1 - Math.cos(e)) / 2;
  if (f < 0.04) return 'New moon';
  if (f > 0.96) return 'Full moon';
  if (Math.abs(f - 0.5) < 0.07) return waxing ? 'First quarter' : 'Last quarter';
  if (f < 0.5) return waxing ? 'Waxing crescent' : 'Waning crescent';
  return waxing ? 'Waxing gibbous' : 'Waning gibbous';
}

function mount(host: HTMLElement, ctx: GameContext) {
  const level = ctx.currentLevel();
  const tier = tierFor(level);
  const steps = STEPS[tier];
  let si = 0;
  let explore = false;
  let attempts = 0, correct = 0, missed = false;
  const t0 = performance.now();
  let tStep = performance.now();
  let tab: Tab = steps[0].tab;

  // ---- state: orbits ----
  let sunMass = 0.6;
  let B: Body[] = [makeSun(sunMass)];
  let launchTag = 0;
  let drag: { x: number; y: number; cx: number; cy: number } | null = null;
  let orbitHint = false;
  // ---- state: stars ----
  let starPick = 1, starT = 0, starPlaying = false, starTm = 0, lastStage = -1;
  const R: number[] = []; { let seed = 7; for (let k = 0; k < 400; k++) { seed = (seed * 16807) % 2147483647; R.push(seed / 2147483647); } }
  // ---- state: moon ----
  let moonA = Math.PI * 0.5;   // screen angle of the Moon around Earth; sun is to the left (angle PI)

  const lesson = h('div.card', { style: { borderLeft: '8px solid var(--science)' } }, h('p', null, ''));
  const task = h('p.center', { style: { fontWeight: '800', minHeight: '44px' } });
  const tabs = h('div.tabs', { style: { padding: '0 0 8px' } });
  const stage = h('div');
  const readout = h('div.row', { style: { justifyContent: 'space-between', fontWeight: '800', minHeight: '24px' } }, h('span', null, ''), h('span.muted.small', null, ''));
  const controls = h('div.stack');
  const choices = h('div.grid2');
  host.append(lesson, task, tabs, stage, readout, controls, choices);
  const S = makeSurface(stage, { aspect: 0.78 });

  function renderTabs() {
    clear(tabs);
    for (const [id, label] of [['orbits', 'Orbits'], ['stars', 'Star Life'], ['moon', 'Moon Phases']] as [Tab, string][]) {
      const locked = !explore && steps[si] && steps[si].tab !== id;
      tabs.append(h('button.tab' + (tab === id ? '.on' : ''), { disabled: locked, style: { opacity: locked ? '.45' : '1' }, onclick: () => { tab = id; renderTabs(); renderControls(); } }, label));
    }
  }

  function renderControls() {
    clear(controls); clear(choices);
    const st = explore ? null : steps[si];
    if (tab === 'orbits') {
      const slider = h('input', { type: 'range', min: '10', max: '100', step: '1', value: String(Math.round(sunMass * 100)), 'aria-label': 'Sun mass' }) as HTMLInputElement;
      slider.addEventListener('input', () => { sunMass = Number(slider.value) / 100; B[0].m = sunMass; });
      controls.append(h('div.row', null, h('span.muted.small', { style: { minWidth: '70px' } }, 'Sun mass'), slider));
      controls.append(h('div.grid2', null,
        h('button.btn.small', { onclick: () => { orbitHint = !orbitHint; ctx.say(orbitHint ? 'The faint ring shows how long a flick needs to be for a steady orbit there.' : ''); } }, 'Hint ring'),
        h('button.btn.small.ghost', { onclick: () => { B = [makeSun(sunMass)]; ctx.say('Fresh sky.'); } }, 'Clear planets'),
      ));
      if (st && st.kind === 'choice') addChoices(st);
    } else if (tab === 'stars') {
      const pills = h('div.grid2', { style: { gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' } });
      STARS.forEach((s, k) => pills.append(h('button.btn.small' + (starPick === k ? '.primary' : ''), { style: { padding: '6px 4px', fontSize: '14px' }, onclick: () => pickStar(k) }, s.label, h('br'), h('span.small', { style: { fontWeight: '600' } }, s.mass))));
      controls.append(pills);
      const life = h('input', { type: 'range', min: '0', max: String(STARS[starPick].st.length), step: '0.01', value: String(starT), 'aria-label': 'Life' }) as HTMLInputElement;
      life.addEventListener('input', () => { starPlaying = false; starT = Number(life.value); });
      const play = h('button.btn.small', { onclick: () => { const n = STARS[starPick].st.length; if (!starPlaying && starT >= n - 0.001) starT = 0; starPlaying = !starPlaying; } }, 'Play / pause');
      controls.append(h('div.row', null, h('span.muted.small', null, 'Life'), life, play));
      controls.append(h('p.small', { style: { minHeight: '60px' } }, ''));
      (controls as any)._life = life;
      if (st && st.kind === 'choice') addChoices(st);
    } else {
      controls.append(h('p.center.muted.small', null, 'Drag the Moon around Earth. The small circle shows what Earth sees.'));
      if (st && st.kind === 'moon') controls.append(h('button.btn.primary.block', { onclick: checkMoon }, 'Check'));
    }
  }

  function addChoices(st: Step) {
    st.choices!.forEach((c, i) => choices.append(h('button.choice', { style: { fontSize: '16px' }, onclick: () => answer(i) }, c)));
  }

  function pickStar(k: number) {
    starPick = k; starT = 0; lastStage = -1; starPlaying = true;
    const st = explore ? null : steps[si];
    renderControls();
    if (st && st.kind === 'starpick') {
      if (st.starOk!.includes(k)) pass(st);
      else fail(st, st.starOk![0] === 0 ? 'Think small. Which star sips its fuel the slowest?' : 'Think big. Only the heaviest stars blow up.');
    }
  }

  function showStep() {
    const st = steps[si];
    (lesson.firstElementChild as HTMLElement).textContent = st.lesson;
    task.textContent = `Step ${si + 1} of 3: ${st.task}`;
    tab = st.tab; missed = false; tStep = performance.now();
    if (st.sunMass !== undefined) { sunMass = st.sunMass; B = [makeSun(sunMass)]; }
    launchTag++;
    renderTabs(); renderControls();
    ctx.speak(st.task);
  }

  function pass(st: Step) {
    if (!missed) { attempts++; correct++; }
    ctx.report({ skill: 'space', level, correct: !missed, hintUsed: missed, ms: performance.now() - tStep, problemId: `sky:${tier}:${si}` });
    ctx.sound('ding'); ctx.say(st.why);
    if (st.kind === 'orbit3') ctx.charm('charm.orbit');
    if (st.kind === 'moon' && si === steps.length - 1) ctx.charm('charm.fullmoon');
    si++;
    if (si >= steps.length) {
      if (steps.some(s => s.kind === 'moon') && tier >= 2) ctx.charm('charm.fullmoon');
      explore = true;
      task.textContent = 'Round complete. All three experiments are open: explore!';
      renderTabs(); renderControls();
      setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 }), 1600);
      return;
    }
    setTimeout(showStep, 1700);
  }

  function fail(st: Step, hint: string) {
    if (!missed) { attempts++; missed = true; ctx.report({ skill: 'space', level, correct: false, hintUsed: false, ms: performance.now() - tStep, problemId: `sky:${tier}:${si}` }); }
    ctx.sound('buzz'); ctx.say(hint);
    void st;
  }

  function answer(i: number) {
    const st = steps[si];
    if (i === st.answer) pass(st); else fail(st, 'Think about what was different between the four stars you could pick.');
  }

  function checkMoon() {
    const st = steps[si]; if (!st || st.kind !== 'moon') return;
    const { e, waxing } = moonGeometry();
    const f = (1 - Math.cos(e)) / 2;
    if (st.moonWant === 'full') { if (f > 0.93) pass(st); else fail(st, 'Full means the whole face is lit. Put the Moon on the far side of Earth from the Sun.'); }
    else if (st.moonWant === 'new') { if (f < 0.07) pass(st); else fail(st, 'New means we see no lit part. Put the Moon between Earth and the Sun.'); }
    else { if (Math.abs(f - 0.5) < 0.09 && waxing) pass(st); else fail(st, Math.abs(f - 0.5) < 0.09 ? 'Half lit, but that is the last quarter. Try the other side of the orbit.' : 'Aim for exactly half lit: a right angle from the Sun.'); }
  }

  // ---- geometry helpers ----
  function moonGeometry(): { e: number; waxing: boolean } {
    const mx = Math.cos(moonA), my = Math.sin(moonA);
    const e = Math.acos(Math.max(-1, Math.min(1, -mx)));   // angle from the Sun direction (-1, 0)
    return { e, waxing: my < 0 };                           // upper half of the orbit on screen = waxing
  }
  function orbitScale(): number { return Math.min(S.w, S.h) * 0.42; }
  function toScreen(x: number, y: number): [number, number] { const s = orbitScale(); return [S.w / 2 + (x - B[0].x) * s, S.h / 2 + (y - B[0].y) * s]; }
  function toWorld(px: number, py: number): [number, number] { const s = orbitScale(); return [B[0].x + (px - S.w / 2) / s, B[0].y + (py - S.h / 2) / s]; }

  const unPointer = attachPointer(S.canvas, {
    down(p) {
      if (tab === 'orbits') { const [wx, wy] = toWorld(p.x, p.y); if (Math.hypot(wx - B[0].x, wy - B[0].y) > 0.08) drag = { x: p.x, y: p.y, cx: p.x, cy: p.y }; }
      else if (tab === 'moon') setMoon(p.x, p.y);
    },
    move(p) { if (tab === 'orbits' && drag) { drag.cx = p.x; drag.cy = p.y; } else if (tab === 'moon') setMoon(p.x, p.y); },
    up(p) {
      if (tab !== 'orbits' || !drag) return;
      const [wx, wy] = toWorld(drag.x, drag.y);
      const s = orbitScale();
      const vx = (p.x - drag.x) / s * 2.2, vy = (p.y - drag.y) / s * 2.2;
      drag = null;
      if (B.length >= 9) { ctx.say('That is plenty of planets. Clear some first.'); return; }
      const pl = makePlanet(B[0], Math.hypot(wx - B[0].x, wy - B[0].y), Math.atan2(wy - B[0].y, wx - B[0].x), 0);
      pl.x = wx; pl.y = wy; pl.vx = B[0].vx + vx; pl.vy = B[0].vy + vy;
      (pl as any).tag = launchTag;
      B.push(pl);
      ctx.sound('pop');
      if (Math.hypot(vx, vy) < 0.05) ctx.say('A gentle drop. It will fall toward the Sun. Try flicking sideways.');
    },
  });
  function setMoon(px: number, py: number) { moonA = Math.atan2(py - S.h / 2, px - S.w * 0.58); }

  function update(dt: number) {
    if (tab === 'orbits' || true) {
      // always advance the sky so a launched planet keeps going while she reads
      for (let i = 0; i < 5; i++) orbitStep(B, DT);
      for (let i = B.length - 1; i > 0; i--) {
        const b = B[i];
        const r = Math.hypot(b.x - B[0].x, b.y - B[0].y);
        const st = explore ? null : steps[si];
        if (b.escaped) {
          B.splice(i, 1);
          if (st && st.kind === 'escape' && (b as any).tag === launchTag) pass(st);
          else if (st && st.kind === 'orbit3' && (b as any).tag === launchTag) fail(st, 'Too fast! It flew away. Flick a little softer.');
          else ctx.say('A planet escaped the system.');
          continue;
        }
        if (r < 0.05) {
          B.splice(i, 1);
          if (st && st.kind === 'orbit3' && (b as any).tag === launchTag) fail(st, 'Too slow. It fell into the Sun. Flick harder, sideways.');
          else if (st && st.kind === 'escape' && (b as any).tag === launchTag) fail(st, 'That one fell in. Flick much harder to escape.');
          continue;
        }
        if (st && st.kind === 'orbit3' && (b as any).tag === launchTag && isStable(b, 3, 0.4)) { pass(st); launchTag++; }
      }
      for (const b of B) if (b.kind === 1) { b.tr.push([b.x, b.y]); if (b.tr.length > 110) b.tr.shift(); }
    }
    starTm += dt;
    if (starPlaying) { const n = STARS[starPick].st.length; starT += dt / 4.5; if (starT >= n) { starT = n; starPlaying = false; } const life = (controls as any)._life as HTMLInputElement | undefined; if (life) life.value = String(starT); }
  }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    c.fillStyle = '#0a0a18'; c.fillRect(0, 0, W, H);
    for (let k = 0; k < 45; k++) { c.globalAlpha = 0.25 + R[k + 150] * 0.5; c.fillStyle = '#fff'; c.beginPath(); c.arc(R[k] * W, R[k + 50] * H, 0.6 + R[k + 100] * 0.9, 0, 7); c.fill(); }
    c.globalAlpha = 1;
    if (tab === 'orbits') drawOrbits(c, W, H);
    else if (tab === 'stars') drawStar(c, W, H);
    else drawMoon(c, W, H);
  }

  function drawOrbits(c: CanvasRenderingContext2D, W: number, H: number) {
    const s = orbitScale();
    if (orbitHint && drag) {
      const [wx, wy] = toWorld(drag.x, drag.y);
      const r = Math.hypot(wx - B[0].x, wy - B[0].y);
      const v = circularSpeed(B[0].m, r) / 2.2 * s;
      c.strokeStyle = 'rgba(255,255,255,.35)'; c.setLineDash([4, 4]); c.lineWidth = 1.5;
      c.beginPath(); c.arc(drag.x, drag.y, v, 0, 7); c.stroke();
      const [sx, sy] = toScreen(B[0].x, B[0].y);
      c.beginPath(); c.arc(sx, sy, r * s, 0, 7); c.stroke(); c.setLineDash([]);
    }
    for (const b of B) {
      if (b.kind === 0) continue;
      c.strokeStyle = 'rgba(29,158,117,.6)'; c.lineWidth = 1.5; c.beginPath();
      b.tr.forEach((q, i) => { const [x, y] = toScreen(q[0], q[1]); if (i) c.lineTo(x, y); else c.moveTo(x, y); }); c.stroke();
    }
    const [sx, sy] = toScreen(B[0].x, B[0].y);
    c.fillStyle = 'rgba(239,159,39,.25)'; c.beginPath(); c.arc(sx, sy, 8 + B[0].m * 22, 0, 7); c.fill();
    c.fillStyle = '#EF9F27'; c.beginPath(); c.arc(sx, sy, 5 + B[0].m * 11, 0, 7); c.fill();
    for (const b of B) { if (b.kind === 0) continue; const [x, y] = toScreen(b.x, b.y); c.fillStyle = (b as any).tag === launchTag ? '#5DCAA5' : '#1D9E75'; c.beginPath(); c.arc(x, y, 4, 0, 7); c.fill(); if (b.laps > 0) { c.fillStyle = '#fff'; c.font = '700 10px Nunito, sans-serif'; c.textAlign = 'left'; c.fillText(`${b.laps} lap${b.laps > 1 ? 's' : ''}`, x + 6, y - 6); } }
    if (drag) { c.strokeStyle = '#fff'; c.lineWidth = 2; c.beginPath(); c.moveTo(drag.x, drag.y); c.lineTo(drag.cx, drag.cy); c.stroke(); c.fillStyle = '#5DCAA5'; c.beginPath(); c.arc(drag.x, drag.y, 4, 0, 7); c.fill(); }
    (readout.children[0] as HTMLElement).textContent = `${B.length - 1} planet${B.length === 2 ? '' : 's'}`;
    (readout.children[1] as HTMLElement).textContent = `Sun mass ${Math.round(B[0].m * 100)}`;
  }

  function disc(c: CanvasRenderingContext2D, x: number, y: number, r: number, col: string, a: number) { c.globalAlpha = a; c.fillStyle = col; c.beginPath(); c.arc(x, y, Math.max(r, 0.1), 0, 7); c.fill(); c.globalAlpha = 1; }
  function starGlow(c: CanvasRenderingContext2D, x: number, y: number, r: number, col: string) { disc(c, x, y, r * 1.7, col, 0.08); disc(c, x, y, r * 1.35, col, 0.16); disc(c, x, y, r, col, 1); disc(c, x, y, r * 0.55, '#fff', 0.25); }
  function sm(u: number) { u = Math.max(0, Math.min(1, u)); return u * u * (3 - 2 * u); }
  function mix(a: string, b: string, u: number) { const A = [1, 3, 5].map(i => parseInt(a.slice(i, i + 2), 16)), Bc = [1, 3, 5].map(i => parseInt(b.slice(i, i + 2), 16)); return `rgb(${A.map((v, i) => Math.round(v + (Bc[i] - v) * u)).join(',')})`; }

  function drawStar(c: CanvasRenderingContext2D, W: number, H: number) {
    const st = STARS[starPick].st, n = st.length, i = Math.min(Math.floor(starT), n - 1), p = Math.min(starT - i, 1), s = st[i], nx = st[i + 1], cx = W / 2, cy = H * 0.5;
    const sc = Math.min(W, H) / 300;
    if (s.t === 'cloud') {
      const cols = ['#AFA9EC', '#ED93B1', '#85B7EB'];
      for (let k = 0; k < 70; k++) { const a = R[200 + k] * Math.PI * 2 + starTm * 0.25 * (0.3 + p), dd = (28 + R[270 + k] * 105) * (1 - 0.82 * sm(p)) * sc; disc(c, cx + Math.cos(a) * dd, cy + Math.sin(a) * dd * 0.8, 2 + R[k] * 2.5, cols[k % 3], 0.75 * (1 - 0.6 * p)); }
      if (p > 0.25 && nx) { const u = sm((p - 0.25) / 0.75); c.globalAlpha = u; starGlow(c, cx, cy, (nx.r || 10) * u * sc, nx.c || '#fff'); c.globalAlpha = 1; }
    } else if (s.t === 'star') {
      let r = s.r!, col = s.c!;
      if (nx && nx.t === 'star' && p > 0.55) { const u = sm((p - 0.55) / 0.45); r = s.r! + (nx.r! - s.r!) * u; col = mix(s.c!, nx.c!, u); }
      if (nx && nx.t === 'wd' && p > 0.6) { const u = sm((p - 0.6) / 0.4); r = s.r! + (5 - s.r!) * u; col = mix(s.c!, '#ffffff', u); }
      if (nx && nx.t === 'boom' && p > 0.9) r *= 1 - 0.6 * (p - 0.9) / 0.1;
      r *= 1 + 0.015 * Math.sin(starTm * 3);
      starGlow(c, cx, cy, r * sc, col);
    } else if (s.t === 'shell') {
      c.globalAlpha = 1 - 0.8 * p;
      for (const [col, lw, r0, r1] of [['#D85A30', 10, 78, 58], ['#5DCAA5', 8, 60, 45], ['#ED93B1', 5, 40, 36]] as [string, number, number, number][]) { c.strokeStyle = col; c.lineWidth = lw * (1 - p) + 2; c.beginPath(); c.arc(cx, cy, (r0 + p * r1) * sc, 0, 7); c.stroke(); }
      c.globalAlpha = 1; starGlow(c, cx, cy, 5, '#fff');
    } else if (s.t === 'boom') {
      const cols = ['#FAC775', '#EF9F27', '#F0997B', '#ED93B1'];
      c.globalAlpha = 1 - p; c.strokeStyle = '#FAC775'; c.lineWidth = 6; c.beginPath(); c.arc(cx, cy, (10 + p * 150) * sc, 0, 7); c.stroke(); c.globalAlpha = 1;
      for (let k = 0; k < 60; k++) { const a = R[200 + k] * Math.PI * 2, dd = p * 185 * (0.45 + 0.55 * R[270 + k]) * sc; disc(c, cx + Math.cos(a) * dd, cy + Math.sin(a) * dd, 2 + R[k] * 3, cols[k % 4], 1 - 0.75 * p); }
      if (nx && nx.t === 'ns') starGlow(c, cx, cy, 4, '#B5D4F4'); else if (p > 0.4) disc(c, cx, cy, 12 * sm((p - 0.4) / 0.6), '#000', 1);
      if (p < 0.14) { c.globalAlpha = 1 - p / 0.14; c.fillStyle = '#fff'; c.fillRect(0, 0, W, H); c.globalAlpha = 1; }
    } else if (s.t === 'wd') starGlow(c, cx, cy, 5, mix('#ffffff', '#B4B2A9', p * 0.8));
    else if (s.t === 'ns') { const a = starTm * 4; c.globalAlpha = 0.5; c.strokeStyle = '#85B7EB'; c.lineWidth = 3; c.lineCap = 'round'; c.beginPath(); c.moveTo(cx - Math.cos(a) * 130 * sc, cy - Math.sin(a) * 45 * sc); c.lineTo(cx + Math.cos(a) * 130 * sc, cy + Math.sin(a) * 45 * sc); c.stroke(); c.globalAlpha = 1; starGlow(c, cx, cy, 4, '#B5D4F4'); }
    else if (s.t === 'bh') { c.globalAlpha = 0.3; c.strokeStyle = '#EF9F27'; c.lineWidth = 5; c.beginPath(); c.ellipse(cx, cy, 34 * sc, 10 * sc, -0.25, 0, 7); c.stroke(); c.globalAlpha = 1; c.strokeStyle = '#EF9F27'; c.lineWidth = 3; c.beginPath(); c.arc(cx, cy, 14.5 * sc, 0, 7); c.stroke(); disc(c, cx, cy, 13 * sc, '#000', 1); }
    if (i !== lastStage) {
      lastStage = i;
      (readout.children[0] as HTMLElement).textContent = s.n;
      (readout.children[1] as HTMLElement).textContent = `Stage ${i + 1} of ${n} · ${STARS[starPick].mass}`;
      const desc = controls.querySelector('p.small') as HTMLElement | null;
      if (desc) desc.textContent = `${s.a}. ${s.d}`;
    }
  }

  function drawMoon(c: CanvasRenderingContext2D, W: number, H: number) {
    const ex = W * 0.58, ey = H * 0.5, orbitR = Math.min(W, H) * 0.3;
    // Sun at the left edge
    c.fillStyle = 'rgba(250,199,117,.2)'; c.beginPath(); c.arc(-W * 0.1, ey, W * 0.26, 0, 7); c.fill();
    c.fillStyle = '#FAC775'; c.beginPath(); c.arc(-W * 0.1, ey, W * 0.2, 0, 7); c.fill();
    // light rays
    c.strokeStyle = 'rgba(250,199,117,.25)'; c.lineWidth = 1; for (let k = -3; k <= 3; k++) { c.beginPath(); c.moveTo(W * 0.1, ey + k * H * 0.12); c.lineTo(W, ey + k * H * 0.12); c.stroke(); }
    // orbit
    c.strokeStyle = 'rgba(255,255,255,.25)'; c.setLineDash([4, 6]); c.beginPath(); c.arc(ex, ey, orbitR, 0, 7); c.stroke(); c.setLineDash([]);
    // Earth: lit on the sun side
    c.fillStyle = '#1b3a6b'; c.beginPath(); c.arc(ex, ey, 16, 0, 7); c.fill();
    c.fillStyle = '#4f8fe6'; c.beginPath(); c.arc(ex, ey, 16, Math.PI / 2, Math.PI * 1.5); c.fill();
    c.fillStyle = '#5fbf6a'; c.beginPath(); c.arc(ex - 5, ey - 3, 5, 0, 7); c.fill();
    // Moon on its orbit, lit half toward the Sun
    const mx = ex + Math.cos(moonA) * orbitR, my = ey + Math.sin(moonA) * orbitR;
    c.fillStyle = '#5a5a66'; c.beginPath(); c.arc(mx, my, 11, 0, 7); c.fill();
    c.fillStyle = '#f1ece6'; c.beginPath(); c.arc(mx, my, 11, Math.PI / 2, Math.PI * 1.5); c.fill();
    c.strokeStyle = '#fff'; c.lineWidth = 2; c.beginPath(); c.arc(mx, my, 16, 0, 7); c.stroke();
    // inset: what Earth sees
    const { e, waxing } = moonGeometry();
    const f = (1 - Math.cos(e)) / 2;
    const ix = W - 52, iy = H - 52, ir = 30;
    c.fillStyle = 'rgba(255,255,255,.08)'; c.beginPath(); c.arc(ix, iy, ir + 10, 0, 7); c.fill();
    const litRight = waxing;
    c.fillStyle = '#5a5a66'; c.beginPath(); c.arc(ix, iy, ir, 0, 7); c.fill();
    c.fillStyle = '#f1ece6'; c.beginPath(); c.arc(ix, iy, ir, litRight ? -Math.PI / 2 : Math.PI / 2, litRight ? Math.PI / 2 : Math.PI * 1.5); c.fill();
    const k = Math.abs(Math.cos(e));
    c.fillStyle = f < 0.5 ? '#5a5a66' : '#f1ece6';
    c.beginPath(); c.ellipse(ix, iy, ir * k, ir, 0, 0, 7); c.fill();
    c.fillStyle = '#fff'; c.font = '800 12px Nunito, sans-serif'; c.textAlign = 'center'; c.fillText('From Earth', ix, iy - ir - 16);
    (readout.children[0] as HTMLElement).textContent = phaseName(e, waxing);
    (readout.children[1] as HTMLElement).textContent = `${Math.round(f * 100)}% lit`;
  }

  const loop = makeLoop(update, draw);
  loop.start();
  showStep();
  return { destroy() { loop.stop(); unPointer(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'science.sky-lab',
  title: 'Sky Lab',
  blurb: 'Flick planets into orbit, watch stars live and die, and turn the Moon through its phases.',
  category: 'science',
  skills: ['space'],
  levelRange: [1, 10],
  unlock: { stars: 11 },
  mount,
};
export default mod;
